import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Heart, Star, TrendingUp, ArrowRight, Loader2, 
  Search, Store, Wrench, X 
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import Breadcrumbs from '../components/Breadcrumbs';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductsPage = () => {
  const { country } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { addToCart } = useCart();
  const [topWeeklyProducts, setTopWeeklyProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          fetch(`${API_URL}/api/products?limit=10&sort_by=sales`),
          fetch(`${API_URL}/api/categories`)
        ]);
        
        const productsData = await productsRes.json();
        const categoriesData = await categoriesRes.json();
        
        setTopWeeklyProducts(productsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Search functionality
  useEffect(() => {
    const searchProducts = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);
      try {
        const response = await fetch(`${API_URL}/api/products/search?q=${encodeURIComponent(searchQuery)}&limit=10`);
        const data = await response.json();
        setSearchResults(data.products || []);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleCategoryClick = (categorySlug) => {
    navigate(`/${country}/products/category/${categorySlug}`);
  };

  const handleProductClick = (productId) => {
    setShowSearchResults(false);
    setSearchQuery('');
    navigate(`/${country}/products/${productId}`);
  };

  const handlePartnerClick = (e, partnerId) => {
    e.stopPropagation();
    navigate(`/${country}/partner/${partnerId}`);
  };

  const handleToggleFavorite = (e, product) => {
    e.stopPropagation();
    toggleFavorite(product);
  };

  const handleExploreCatalog = () => {
    navigate(`/${country}/products/catalog`);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  const sortedCategories = [...categories].sort((a, b) => {
    const scoreA = (a.total_sales || 0) + (a.average_rating || 0) * 100;
    const scoreB = (b.total_sales || 0) + (b.average_rating || 0) * 100;
    return scoreB - scoreA;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-aug-cream flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-aug-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aug-cream">
      <Header />
      <WhatsAppButton />

      <div className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-xl">
          <Breadcrumbs
            items={[
              { label: 'Home', path: `/${country}` },
              { label: 'Produtos Inovadores', path: `/${country}/products` }
            ]}
          />

          {/* Hero */}
          <div className="mb-8 text-center">
            <h1 className="text-5xl font-bold mb-4" style={{ color: '#1B5E20' }}>
              Produtos Inovadores
            </h1>
            <p className="text-xl text-aug-gray-medium max-w-3xl mx-auto mb-8">
              Descubra produtos físicos e digitais selecionados. Qualidade garantida e avaliações reais de clientes.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar produtos por nome, código ou descrição..."
                  className="w-full pl-12 pr-12 py-4 rounded-xl border-2 border-gray-200 focus:border-aug-blue focus:outline-none text-lg shadow-sm"
                  data-testid="product-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
                {isSearching && (
                  <Loader2 className="absolute right-12 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-aug-blue" />
                )}
              </div>

              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 max-h-96 overflow-y-auto z-50">
                  <div className="p-2">
                    <p className="text-sm text-gray-500 px-3 py-2">
                      {searchResults.length} resultado(s) encontrado(s)
                    </p>
                    {searchResults.map((product) => (
                      <div
                        key={product.id}
                        onClick={() => handleProductClick(product.id)}
                        className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                      >
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                          {product.images?.[0] ? (
                            <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs text-gray-400">Sem img</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">{product.name}</h4>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-bold text-green-700">
                              R$ {product.regular_price?.toFixed(2)}
                            </span>
                            {product.partner_name && (
                              <span className="text-gray-400">• {product.partner_name}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {showSearchResults && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 p-6 text-center z-50">
                  <p className="text-gray-500">Nenhum produto encontrado para "{searchQuery}"</p>
                </div>
              )}
            </div>
          </div>

          {/* Top 10 da Semana */}
          <section className="mb-16">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-7 h-7 text-orange-500" />
                <div>
                  <h2 className="text-3xl font-bold text-aug-gray-dark">
                    Top 10 da Semana
                  </h2>
                  <p className="text-aug-gray-medium">Produtos mais vendidos nos últimos 7 dias</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {topWeeklyProducts.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  index={index}
                  onProductClick={handleProductClick}
                  onPartnerClick={handlePartnerClick}
                  onToggleFavorite={handleToggleFavorite}
                  isFavorite={isFavorite(product.id)}
                  country={country}
                />
              ))}
            </div>
          </section>

          {/* Categorias em Destaque */}
          <section className="mb-16">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-aug-gray-dark mb-2">
                Categorias em Destaque
              </h2>
              <p className="text-aug-gray-medium">Navegue por categoria e encontre o que precisa</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedCategories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => handleCategoryClick(category.slug)}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-16 h-16 bg-aug-blue-light rounded-lg flex items-center justify-center group-hover:bg-aug-blue transition-colors">
                      <img
                        src={category.image_url || 'https://via.placeholder.com/64'}
                        alt={category.name}
                        className="w-10 h-10 object-contain"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-bold">{category.average_rating || 0}</span>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-aug-gray-dark mb-2 group-hover:text-aug-blue transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-sm text-aug-gray-medium mb-3 line-clamp-2">
                    {category.description}
                  </p>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-aug-gray-medium">
                      {category.products_count} produtos
                    </span>
                    <div className="flex items-center gap-1 text-aug-blue group-hover:translate-x-1 transition-transform">
                      <span className="font-medium">Explorar</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Catálogo Completo */}
          <section className="bg-gradient-to-r from-aug-blue to-aug-green text-white rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Explore Nosso Catálogo Completo
            </h2>
            <p className="text-xl mb-6 max-w-2xl mx-auto opacity-90">
              Navegue por todos os produtos em formato carrossel estilo Netflix
            </p>
            <button
              onClick={handleExploreCatalog}
              className="bg-white text-aug-blue px-8 py-4 rounded-xl font-semibold text-lg hover:bg-aug-cream transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Ver Catálogo Completo
            </button>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Product Card Component with Partner Info
const ProductCard = ({ product, index, onProductClick, onPartnerClick, onToggleFavorite, isFavorite, country }) => {
  const navigate = useNavigate();
  const [recommendedTech, setRecommendedTech] = useState(null);

  // Fetch recommended technician on hover (lazy load)
  const fetchRecommendedTech = async () => {
    if (recommendedTech) return;
    
    try {
      // For now, we'll use a simple approach - in production, you'd get user's location
      const response = await fetch(`${API_URL}/api/service-providers/recommended?category_id=${product.category_id}`);
      const data = await response.json();
      if (data.provider) {
        setRecommendedTech(data);
      }
    } catch (error) {
      console.error('Error fetching tech:', error);
    }
  };

  return (
    <div
      onClick={() => onProductClick(product.id)}
      onMouseEnter={fetchRecommendedTech}
      className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group relative"
      data-testid={`product-card-${product.id}`}
    >
      {/* Ranking Badge */}
      {typeof index === 'number' && (
        <div className="absolute top-2 left-2 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm z-10">
          {index + 1}
        </div>
      )}

      {/* Image */}
      <div className="relative h-48 overflow-hidden bg-gray-100">
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <span className="text-xs">Sem imagem</span>
          </div>
        )}
        
        {/* Favorite Button */}
        <button
          onClick={(e) => onToggleFavorite(e, product)}
          className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg"
          data-testid={`favorite-btn-${product.id}`}
        >
          <Heart
            className={`w-4 h-4 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`}
          />
        </button>
      </div>

      {/* Info */}
      <div className="p-3">
        {/* Partner Badge */}
        {product.partner_name && (
          <div 
            onClick={(e) => onPartnerClick(e, product.partner_id)}
            className="flex items-center gap-1.5 mb-2 text-xs text-gray-500 hover:text-aug-blue transition-colors"
          >
            <Store className="w-3 h-3" />
            <span className="truncate">{product.partner_name}</span>
          </div>
        )}

        <h3 className="text-sm font-semibold text-aug-gray-dark line-clamp-2 mb-2 min-h-[2.5rem]">
          {product.name}
        </h3>
        
        {/* Rating */}
        <div className="flex items-center gap-1 mb-2">
          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
          <span className="text-xs font-semibold">{product.average_rating || 0}</span>
          <span className="text-xs text-aug-gray-medium">({product.reviews_count || 0})</span>
        </div>

        {/* Price */}
        <p className="text-lg font-bold mb-2" style={{ color: '#1B5E20' }}>
          R$ {(product.sale_price || product.regular_price).toFixed(2)}
        </p>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={(e) => onPartnerClick(e, product.partner_id)}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 px-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-700 transition-colors"
            data-testid={`view-partner-btn-${product.id}`}
          >
            <Store className="w-3 h-3" />
            <span>Empresa</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (recommendedTech?.provider) {
                navigate(`/${country}/services/${recommendedTech.provider.id}`);
              } else {
                navigate(`/${country}/services`);
              }
            }}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 px-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-xs font-medium text-blue-700 transition-colors"
            data-testid={`find-tech-btn-${product.id}`}
          >
            <Wrench className="w-3 h-3" />
            <span>Técnico</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
